import { Injectable, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, catchError, throwError, BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
    UserProfile,
    AuthResponse,
    UserLoginRequest,
    UserRegisterRequest,
    UserProfileUpdate,
    ChangePasswordRequest
} from '../../models/user.model';

const TOKEN_KEY = 'sqw_user_token';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private apiUrl = environment.apiUrl;

    // Reactive state using signals
    private _token = signal<string | null>(this.getStoredToken());
    private _user = signal<UserProfile | null>(null);
    private _loading = signal<boolean>(false);

    // Public computed signals
    readonly token = this._token.asReadonly();
    readonly user = this._user.asReadonly();
    readonly loading = this._loading.asReadonly();
    readonly isAuthenticated = computed(() => !!this._token());

    // Observable for components that prefer RxJS
    private userSubject = new BehaviorSubject<UserProfile | null>(null);
    readonly user$ = this.userSubject.asObservable();

    constructor(
        private http: HttpClient,
        private router: Router
    ) {
        // Load user profile if token exists
        if (this._token()) {
            this.loadUserProfile();
        }
    }

    /**
     * Register a new user
     */
    register(data: UserRegisterRequest): Observable<AuthResponse> {
        this._loading.set(true);
        return this.http.post<AuthResponse>(`${this.apiUrl}/user/register`, data).pipe(
            tap(response => {
                this.setToken(response.token);
                this.loadUserProfile();
                this._loading.set(false);
            }),
            catchError(err => {
                this._loading.set(false);
                return throwError(() => err);
            })
        );
    }

    /**
     * Login existing user
     */
    login(data: UserLoginRequest): Observable<AuthResponse> {
        this._loading.set(true);
        return this.http.post<AuthResponse>(`${this.apiUrl}/user/login`, data).pipe(
            tap(response => {
                this.setToken(response.token);
                this.loadUserProfile();
                this._loading.set(false);
            }),
            catchError(err => {
                this._loading.set(false);
                return throwError(() => err);
            })
        );
    }

    /**
     * Logout user
     */
    logout(): void {
        this.http.post(`${this.apiUrl}/user/logout`, {}).subscribe({
            complete: () => this.clearAuth(),
            error: () => this.clearAuth()
        });
    }

    /**
     * Get current user profile
     */
    getProfile(): Observable<UserProfile> {
        return this.http.get<UserProfile>(`${this.apiUrl}/user/me`);
    }

    /**
     * Update user profile
     */
    updateProfile(data: UserProfileUpdate): Observable<void> {
        return this.http.put<void>(`${this.apiUrl}/user/me`, data).pipe(
            tap(() => this.loadUserProfile())
        );
    }

    /**
     * Change password
     */
    changePassword(data: ChangePasswordRequest): Observable<void> {
        return this.http.put<void>(`${this.apiUrl}/user/me/password`, data);
    }

    /**
     * Get token for HTTP interceptor
     */
    getToken(): string | null {
        return this._token();
    }

    /**
     * Load user profile from API
     */
    private loadUserProfile(): void {
        this.getProfile().subscribe({
            next: (user) => {
                this._user.set(user);
                this.userSubject.next(user);
            },
            error: () => {
                // Token invalid, clear auth
                this.clearAuth();
            }
        });
    }

    /**
     * Store token
     */
    private setToken(token: string): void {
        this._token.set(token);
        sessionStorage.setItem(TOKEN_KEY, token);
    }

    /**
     * Get token from storage
     */
    private getStoredToken(): string | null {
        return sessionStorage.getItem(TOKEN_KEY);
    }

    /**
     * Clear all auth state
     */
    private clearAuth(): void {
        this._token.set(null);
        this._user.set(null);
        this.userSubject.next(null);
        sessionStorage.removeItem(TOKEN_KEY);
        this.router.navigate(['/']);
    }
}
